"use client";

import { useAppEvents } from "@jace/insights";
import { Button, Icon, cn } from "@jace/ui";
import { useState } from "react";

// Type definitions
export type Frequency = "monthly" | "yearly";
export type PlanId = "plus" | "pro" | "ultra";
export type FrequencyOption = {
	value: Frequency;
	label: string;
};
export type Price = {
	monthly: number;
	yearly: number;
};
export type Tier = {
	name: string;
	id: PlanId;
	description: string;
	price: Price;
	features: string[];
	mostPopular: boolean;
};

// Constants
export const DISCOUNT_PERCENTAGE = 40;
export const FREQUENCIES: readonly FrequencyOption[] = [
	{ value: "yearly", label: "Yearly" },
	{ value: "monthly", label: "Monthly" },
];
export const TIERS: readonly Tier[] = [
	{
		name: "Plus",
		id: "plus",
		description: "Level up your productivity and creativity",
		price: { monthly: 20, yearly: 200 },
		features: [
			// "10 emails auto-drafted daily — Just review and send",
			"Jace drafts 24/7—so when you open your inbox, drafts are waiting for you",
			// "Jace drafts up to 5 emails daily—so when you open your inbox, drafts are waiting for you",
			"AI agent that drafts emails in your unique style",
			"Ask questions about your emails and attachments",
			"Auto-label and organize every email effortlessly",
			"Create custom labels with a simple prompt",
			"Sync drafts and labels with Gmail for seamless mobile access",
			"Schedule meetings effortlessly—Jace suggests the best time slots",
			"Automate your inbox with intelligent rules",
			"Stay on top of your day with daily briefings",
			"AI-powered search that understands context, not just keywords",
			"Supercharge your workflow with seamless integrations",
			"3 years of email history",
			"Secure. Private. Encrypted.",
		],
		mostPopular: false,
	},
	{
		name: "Pro",
		id: "pro",
		description: "Advanced features for professionals",
		price: { monthly: 65, yearly: 650 },
		features: [
			"Jace drafts 24/7—so when you open your inbox, drafts are waiting for you",
			"AI agent that drafts emails in your unique style",
			"Ask questions about your emails and attachments",
			"Auto-label and organize every email effortlessly",
			"Create custom labels with a simple prompt",
			"Sync drafts and labels with Gmail for seamless mobile access",
			"Schedule meetings effortlessly—Jace suggests the best time slots",
			"Automate your inbox with intelligent rules",
			"Stay on top of your day with daily briefings",
			"AI-powered search that understands context, not just keywords",
			"Supercharge your workflow with seamless integrations",
			"3 years of email history",
			"Secure. Private. Encrypted.",
			"3x more limits",
			"Priority support",
		],
		mostPopular: false,
	},
	{
		name: "Ultra",
		id: "ultra",
		description: "Higher usage limits for power users",
		price: { monthly: 149, yearly: 1490 },
		features: [
			"Jace drafts 24/7—so when you open your inbox, drafts are waiting for you",
			"AI agent that drafts emails in your unique style",
			"Ask questions about your emails and attachments",
			"Auto-label and organize every email effortlessly",
			"Create custom labels with a simple prompt",
			"Sync drafts and labels with Gmail for seamless mobile access",
			"Schedule meetings effortlessly—Jace suggests the best time slots",
			"Automate your inbox with intelligent rules",
			"Stay on top of your day with daily briefings",
			"AI-powered search that understands context, not just keywords",
			"Supercharge your workflow with seamless integrations",
			"10 years of email history",
			"Secure. Private. Encrypted.",
			"10x more limits",
			"Priority support",
		],
		mostPopular: false,
	},
];

/**
 * Calculates the monthly price from a yearly price
 */
export function calculateMonthlyPrice(yearlyPrice: number): number {
	return yearlyPrice / 12;
}

/**
 * Calculates the original price based on the discount percentage
 */
export function calculateOriginalPrice(
	discountedPrice: number,
	isAnnual: boolean,
): number {
	return (
		discountedPrice / (1 - DISCOUNT_PERCENTAGE / 100) / (isAnnual ? 12 : 1)
	);
}

/**
 * Formats a price number as a currency string
 */
export function formatPrice(price: number): string {
	return `$${price.toFixed(price % 1 === 0 ? 0 : 2)}`;
}

/**
 * Formats the original price as a currency string without cents
 */
export function formatOriginalPrice(price: number): string {
	return `$${Math.round(price)}`;
}

/**
 * Frequency selector component
 */
function FrequencySelector({
	frequency,
	setFrequency,
	tier,
}: {
	frequency: FrequencyOption;
	setFrequency: (frequency: FrequencyOption) => void;
	tier: Tier;
}) {
	return (
		<div className="mt-6 grid grid-cols-1 gap-3">
			{FREQUENCIES.map((option) => {
				const isAnnual = option.value === "yearly";
				const isSelected = frequency.value === option.value;
				const currentPrice = isAnnual ? tier.price.yearly : tier.price.monthly;
				const originalPrice = calculateOriginalPrice(currentPrice, isAnnual);

				return (
					<button
						key={option.value}
						type="button"
						className={cn(
							"relative rounded-xl p-3 cursor-pointer border-2 transition-all w-full text-left",
							isSelected
								? "border-surface-highlight bg-surface-highlight/5"
								: "border-white/10 hover:border-white/20",
						)}
						onClick={() => setFrequency(option)}
						aria-pressed={isSelected}
					>
						<div className="flex items-center justify-between">
							<div className="flex items-center">
								<div
									className={cn(
										"w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center flex-shrink-0",
										isSelected ? "border-surface-highlight" : "border-white/30",
									)}
								>
									{isSelected && (
										<div className="w-3 h-3 rounded-full bg-surface-highlight" />
									)}
								</div>
								<div>
									{isAnnual ? (
										<div className="flex flex-col h-14">
											<div className="flex items-center">
												<span className="text-sm sm:text-lg font-semibold tracking-tight line-through text-text-body-muted mr-2">
													{formatOriginalPrice(originalPrice)}
												</span>
												<span className="text-sm sm:text-lg font-semibold tracking-tight text-white">
													{formatPrice(calculateMonthlyPrice(currentPrice))}
													/month
												</span>
											</div>
											<p className="text-text-muted text-xs sm:text-sm mt-0.5 whitespace-nowrap">
												Pay yearly, 7 day free trial
											</p>
										</div>
									) : (
										<div className="flex flex-col h-14">
											<div className="flex items-center">
												<span className="text-sm sm:text-lg font-semibold tracking-tight line-through text-text-body-muted mr-2">
													{formatOriginalPrice(originalPrice)}
												</span>
												<span className="text-sm sm:text-lg font-semibold tracking-tight text-white">
													{formatPrice(currentPrice)}/month
												</span>
											</div>
											<p className="text-text-muted text-xs sm:text-sm mt-0.5 whitespace-nowrap">
												Pay monthly, 7 day free trial
											</p>
										</div>
									)}
								</div>
							</div>
							{isSelected && (
								<span className="text-xs md:text-sm font-bold bg-surface-highlight text-black px-1.5 py-0.5 rounded-md whitespace-nowrap flex-shrink-0 ml-1">
									{DISCOUNT_PERCENTAGE}% OFF
								</span>
							)}
						</div>
					</button>
				);
			})}
		</div>
	);
}

/**
 * Feature list component
 */
function FeatureList({ features }: { features: string[] }) {
	return (
		<ul className="mt-4 space-y-3 text-sm sm:text-md/6 text-white xl:mt-6">
			{features.map((feature) => (
				<li key={feature} className="flex items-start">
					<Icon
						kind="done"
						aria-hidden="true"
						className="h-5 w-5 text-emerald-500 mt-0.5 mr-3 flex-shrink-0"
					/>

					{feature}
				</li>
			))}
		</ul>
	);
}

/**
 * PricingTier component
 */
function PricingTier({
	tier,
	onPlanClick,
	buttonText,
	belowButtonText = "Unconditional 14-day money back guarantee",
}: {
	tier: Tier;
	onPlanClick: (tierId: PlanId, frequency: Frequency) => void;
	buttonText?: string;
	belowButtonText?: string;
}) {
	const [frequency, setFrequency] = useState<FrequencyOption>(FREQUENCIES[0]);

	return (
		<div
			className={cn(
				tier.mostPopular
					? "bg-surface ring-2 ring-highlight-yellow"
					: "ring-1 ring-white/10",
				"bg-surface-bg rounded-3xl shadow-lg ring-1 ring-white/10 overflow-hidden px-4 py-6 sm:px-8 sm:py-8 xl:p-10",
			)}
		>
			<div className="flex items-center justify-between gap-x-4">
				<h2 id={tier.id} className="text-3xl/8 font-semibold text-white">
					{tier.name}
				</h2>
				{tier.mostPopular ? (
					<p className="rounded-full bg-highlight-yellow px-2.5 py-1 text-xs/5 font-semibold">
						Most popular
					</p>
				) : null}
			</div>
			<p className="mt-2 text-md sm:text-lg/6 text-text-muted">
				{tier.description}
			</p>

			<FrequencySelector
				frequency={frequency}
				setFrequency={setFrequency}
				tier={tier}
			/>

			<Button
				intent={tier.mostPopular ? "brand" : "secondary"}
				size="lg"
				className="mt-6 w-full cursor-pointer"
				onClick={() => onPlanClick(tier.id, frequency.value)}
			>
				{buttonText || `Choose ${tier.name} Plan`}
				{/* {buttonText || `Redeem ${DISCOUNT_PERCENTAGE}% OFF`} */}
			</Button>
			<div className="text-center text-text-muted text-sm mt-2">
				{belowButtonText}
			</div>

			<FeatureList features={tier.features} />
		</div>
	);
}

export function TrustBadges() {
	return (
		<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
			<div className="flex flex-col items-center text-center p-4">
				<Icon kind="help" className="text-surface-highlight mb-3 w-8 h-8" />
				<h3 className="text-lg font-semibold text-white mb-2">24/7 support</h3>
				<p className="text-text-muted text-sm">
					Get help at all times with priority support
				</p>
			</div>
			<div className="flex flex-col items-center text-center p-4">
				<Icon kind="done" className="text-surface-highlight mb-3 w-8 h-8" />
				<h3 className="text-lg font-semibold text-white mb-2">
					Money-back guarantee
				</h3>
				<p className="text-text-muted text-sm">
					Love it or get full refund within 14-days
				</p>
			</div>
			<div className="flex flex-col items-center text-center p-4">
				<Icon kind="shield" className="text-surface-highlight mb-3 w-8 h-8" />
				<h3 className="text-lg font-semibold text-white mb-2">
					Privacy protection
				</h3>
				<p className="text-text-muted text-sm">
					Your data is stored securely on the cloud with encryption
				</p>
			</div>
			<div className="flex flex-col items-center text-center p-4">
				<Icon kind="locked" className="text-surface-highlight mb-3 w-8 h-8" />
				<h3 className="text-lg font-semibold text-white mb-2">
					Secure checkout
				</h3>
				<p className="text-text-muted text-sm">
					256-bit encrypted checkout with Stripe
				</p>
			</div>
		</div>
	);
}

export function PricingTable({
	onPlanClick,
	buttonText,
	belowButtonText,
	showUltraPlan = false,
}: {
	onPlanClick: (tierId: PlanId, frequency: Frequency) => void;
	buttonText?: string;
	belowButtonText?: string;
	showUltraPlan?: boolean;
}) {
	const { emit } = useAppEvents();
	const [showUltraUnavailableMessage, setShowUltraUnavailableMessage] =
		useState(false);

	// Wrapper for the onPlanClick handler that also emits an event
	const handlePlanClick = (tierId: PlanId, frequency: Frequency) => {
		emit({
			type: "select_plan_click",
			payload: {
				plan: tierId,
				frequency: frequency === "yearly" ? "annually" : frequency,
			},
		});

		// Special handling for Ultra plan
		if (tierId === "ultra") {
			setShowUltraUnavailableMessage(true);
			// Auto-hide the message after 5 seconds
			setTimeout(() => {
				setShowUltraUnavailableMessage(false);
			}, 5000);
			return;
		}

		// Call the provided onPlanClick handler for normal plans
		onPlanClick(tierId, frequency);
	};

	// Filter tiers based on whether to show Ultra plan
	const displayTiers = showUltraPlan
		? TIERS
		: TIERS.filter((tier) => tier.id !== "ultra");

	return (
		<>
			{/* Ultra unavailable message modal */}
			{showUltraUnavailableMessage && (
				<div className="fixed inset-0 z-50 flex items-center justify-center">
					<div
						className="fixed inset-0 bg-black/30 backdrop-blur-sm"
						onClick={() => setShowUltraUnavailableMessage(false)}
					/>
					<div className="relative bg-surface-bg p-6 rounded-lg shadow-lg max-w-sm text-center">
						<h3 className="text-xl font-bold text-white mb-3">Coming Soon</h3>
						<p className="text-text-muted mb-4">
							Sorry, the Ultra plan is not available yet. Please subscribe to
							Pro for now.
						</p>
						<Button
							intent="secondary"
							size="sm"
							onClick={() => setShowUltraUnavailableMessage(false)}
						>
							Got it
						</Button>
					</div>
				</div>
			)}

			<div
				className={`isolate mt-8 mx-auto grid gap-8 ${showUltraPlan ? "grid-cols-3" : "grid-cols-1 lg:grid-cols-2"}`}
			>
				{/* Pricing tiers */}
				{displayTiers.map((tier) => (
					<PricingTier
						key={tier.id}
						tier={tier}
						onPlanClick={handlePlanClick}
						buttonText={buttonText}
						belowButtonText={belowButtonText}
					/>
				))}
			</div>
			<p className="mt-8 text-center text-md text-text-body-muted">
				<strong className="text-surface-highlight">
					Save {DISCOUNT_PERCENTAGE}%
				</strong>{" "}
				with our limited-time offer
			</p>
			<div className="mt-8">
				<TrustBadges />
			</div>
		</>
	);
}
